import React, { useEffect, useState } from 'react';
import { GlobalWorkerOptions, getDocument, PDFDocumentProxy } from 'pdfjs-dist';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import InvoiceForm from '../molecules/InvoiceForm';
import StepIndicator from '../molecules/StepIndicator';
import PDFPreview from '../molecules/PDFPreview';
import SubmitForm from '../molecules/SubmitForm';
import dayjs from 'dayjs';
import { Spin, Modal } from 'antd';
import { pdfjs } from 'react-pdf';
import moment from 'moment';
import { getVendors, insertVendor, getCompanies } from '../../api/VendorsContainer';
import {
    ACCOUNTING_EMAIL_ROLE,
    COMPLIANCE_CHECK_ROLE,
    FINANCIAL_CONTROLLER_ROLE,
    FIRST_APPROVER_ROLE,
    getFinancialControllers,
} from '../../api/FinancialControllersContainer';
import { findMatchingVendor } from '../../utils/vendorUtils';
import { controllerExistsInUsers, getControllerDisplayName } from '../../utils/userUtils';
import { useNavigate } from 'react-router-dom'; // Import useHistory hook from React Router
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import { useAuth } from '../../context/AuthContext';
import { getUsers } from '../../api/UsersContainer';
import { uploadInvoicePdf, uploadAzureBlob } from '../../api/PdfUpload';
import { insertInvoice, checkInvoiceExists } from '../../api/InvoiceContainer';
import { sendEmail } from '../../api/NodeServer';
import { folderVariables, folderVariablesSodaWorld } from '../../api/oneDriveConfig';
import FinancialApproval from '../molecules/FinancialApproval';
import { extractInvoice } from '../../api/AiInvoiceParser';
import { PDFDocument } from 'pdf-lib';

//GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.1.392/pdf.worker.min.mjs`;

const Invoice = () => {
    const [loading, setLoading] = useState(false);
    const [vendor, setVendor] = useState('');
    const [isNewVendor, setIsNewVendor] = useState(false);
    const [vendors, setVendors] = useState([]);
    const [company, setCompany] = useState('');
    const [companies, setCompanies] = useState([]);
    const [controllers, setControllers] = useState([]);
    const [financialController, setFinancialController] = useState('');
    const [invoiceNumber, setInvoiceNumber] = useState('');
    const [invoiceDate, setInvoiceDate] = useState(dayjs());
    const [dueDate, setDueDate] = useState('');
    const [currency, setCurrency] = useState('');
    const [amount, setAmount] = useState('');
    const [activeTab, setActiveTab] = useState('upload');
    const [file, setFile] = useState(null);
    const [base64Jpg, setBase64Jpg] = useState(null);
    const [pdfContent, setPdfContent] = useState(null);
    const [comments, setComments] = useState('');
    const navigate = useNavigate();
    const { user, accessToken, refreshToken, setAccessToken, role, validateToken } = useAuth();
    const [sender, setSender] = useState(user?.name);
    const [errorMessages, setErrorMessages] = useState([]);

    // useEffect(() => {
    //     const checkTokenValidity = async () => {
    //         try {
    //             // Validate the current token
    //             const response = await validateToken();

    //             setAccessToken(response.accessToken);
    //         } catch (error) {
    //             console.error('Error validating token:', error);
    //         }
    //     };

    //     checkTokenValidity();

    //     // You can add more dependencies if needed, e.g., accessTokenRef.current, refreshTokenRef.current
    // }, []);


    useEffect(() => {
        const fetchData = async () => {
            const data = await getVendors();
            setVendors(data);
        };

        fetchData();
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            const data = await getCompanies();
            setCompanies(data);

            const controllersData = await getFinancialControllers();
            setControllers(controllersData);

            const defaultController = (
                controllersData.find((controller) => controller.role === FINANCIAL_CONTROLLER_ROLE) ||
                controllersData.find((controller) => controller.role === FIRST_APPROVER_ROLE)
            );
            setFinancialController(defaultController?.controller || '');
        };

        fetchData();
    }, []);

    const goBack = () => {
        setActiveTab('upload');
        setFile(null);
        setAmount('');
        setCurrency('');
        setDueDate('');
        setInvoiceDate('');
        setInvoiceNumber('');
        setVendor('');
    };

    const goBackSubmit = () => {
        setActiveTab('upload');
    };

    const handleVendorChange = (newVendor) => {
        setVendor(newVendor);
        const selectedVendor = vendors.find(v => v.vendor === newVendor) || findMatchingVendor(vendors, newVendor);
        if (selectedVendor) {
            setIsNewVendor(selectedVendor.creationDate ? new Date(selectedVendor.creationDate).toDateString() === new Date().toDateString() : false);
        }
    };

    const handleCompanyChange = (newCompany) => {
        setCompany(newCompany);
    };

    const handleInvoiceNumberChange = (e) => {
        setInvoiceNumber(e.target.value);
    };

    const handleInvoiceDateChange = (dateString) => {
        if (dateString === null) {
            return;
        }
        setInvoiceDate(dayjs(dateString));
    };

    const handleDueDateChange = (dateString) => {
        if (dateString === null) {
            return;
        }
        setDueDate(dayjs(dateString));
    };

    const handleCurrencyChange = (value) => {
        setCurrency(value);
    };

    const handleAmountChange = (e) => {
        setAmount(e.target.value);
    };

    const showAlert = (type, message) => {
        Modal[type]({
            title: 'Alert',
            content: message,
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (vendor === '') {
            showAlert('error', 'Please select a vendor');
            return;
        }

        if (company === '') {
            showAlert('error', 'Please select a company before proceeding');
            return;
        }

        if (currency === '') {
            showAlert('error', 'Please select a currency');
            return;
        }

        try {
            const vendorData = vendors.find(v => v.vendor === vendor) || findMatchingVendor(vendors, vendor);

            if (!vendorData) {
                showAlert('error', 'Please select a valid vendor');
                return;
            }

            const vendor_id = vendorData.id;
            const response = await checkInvoiceExists(invoiceNumber, vendor_id);

            if (response) {
                //alert('Invoice already exists');
                showAlert('error', 'Invoice already exists');
            } else {
                setActiveTab('submit');
            }
        } catch (error) {
        }
    };

    const handleNewVendor = async (newVendor) => {
        const vendorName = newVendor?.toString().trim();

        if (!vendorName) {
            showAlert('error', 'Please enter a vendor name');
            return false;
        }

        const existingVendor = findMatchingVendor(vendors, vendorName);

        if (existingVendor) {
            setVendor(existingVendor.vendor);
            setIsNewVendor(existingVendor.creationDate ? new Date(existingVendor.creationDate).toDateString() === new Date().toDateString() : false);
            showAlert('info', `Vendor already exists as "${existingVendor.vendor}"`);
            return existingVendor;
        }

        setLoading(true);
        try {
            const creationDate = new Date();
            const insertedVendor = await insertVendor({ vendor: vendorName, type: null, recurring: false, creationDate: creationDate });
            const newVendorData = { ...insertedVendor, vendor: insertedVendor.vendor || vendorName };
            setIsNewVendor(true);
            setVendors((currentVendors) => {
                if (findMatchingVendor(currentVendors, newVendorData.vendor)) {
                    return currentVendors;
                }

                return [...currentVendors, newVendorData];
            });
            setVendor(newVendorData.vendor);
            return newVendorData;
        } catch (error) {
            if (error.code === 'DUPLICATE_VENDOR') {
                const existingVendor = error.existingVendor;
                setVendor(existingVendor.vendor);
                setIsNewVendor(existingVendor.creationDate ? new Date(existingVendor.creationDate).toDateString() === new Date().toDateString() : false);
                setVendors((currentVendors) => {
                    if (currentVendors.some((vendor) => vendor.id === existingVendor.id)) {
                        return currentVendors;
                    }

                    return [...currentVendors, existingVendor];
                });
                showAlert('info', `Vendor already exists as "${existingVendor.vendor}"`);
                return existingVendor;
            }

            showAlert('error', error.message || 'Vendor could not be created');
            return false;
        } finally {
            setLoading(false);
        }
    };

    const readFileAsArrayBuffer = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => resolve(e.target.result);
            reader.onerror = (error) => reject(error);
            reader.readAsDataURL(file);
        });
    };

    useEffect(() => {
        const loadFileData = async () => {
            if (file) {
                try {
                    const fileData = await readFileAsArrayBuffer(file);
                    setPdfContent(fileData);
                    //parsePDFInvoice(file);
                    const fileReader = new FileReader();
                    fileReader.onload = async (e) => {
                        const typedArray = new Uint8Array(e.target.result);
                        const pdf = await getDocument(typedArray).promise;
                        const page = await pdf.getPage(1); // Extract the first page

                        const viewport = page.getViewport({ scale: 1.5 });
                        const canvas = document.createElement('canvas');
                        const context = canvas.getContext('2d');
                        canvas.width = viewport.width;
                        canvas.height = viewport.height;

                        const renderContext = {
                            canvasContext: context,
                            viewport: viewport,
                        };

                        await page.render(renderContext).promise;
                        const dataUrl = canvas.toDataURL('image/jpeg'); // Convert canvas to JPEG Base64
                        const base64String = dataUrl.split(',')[1];
                        setBase64Jpg(base64String);
                    };
                    fileReader.readAsArrayBuffer(file);
                } catch (error) {
                }
            }
        };

        loadFileData();
    }, [file]);

    useEffect(() => {
        if (base64Jpg) {
            setLoading(true);

            const parseInvoice = async () => {
                try {
                    let extractedData = await extractInvoice(base64Jpg);

                    // STEP 1: If it's a string, clean and parse it
                    if (typeof extractedData === 'string') {
                        // Remove any code fencing/backticks and leading "json"
                        extractedData = extractedData
                            .replace(/```/g, '')
                            .replace(/json/i, '') // case-insensitive
                            .trim();

                        try {
                            extractedData = JSON.parse(extractedData);
                        } catch (err) {
                            console.error('Failed to parse JSON:', err, extractedData);
                            setLoading(false);
                            setBase64Jpg(null);
                            return;
                        }
                    }

                    const currentVendors = vendors.length > 0 ? vendors : await getVendors();

                    if (vendors.length === 0) {
                        setVendors(currentVendors);
                    }

                    await findBestMatchVendor(extractedData?.companyName, currentVendors);

                    setInvoiceNumber(extractedData.invoiceNumber);

                    if (extractedData.invoiceDate && extractedData.invoiceDate !== 'not_found') {
                        const date = moment(extractedData.invoiceDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
                        setInvoiceDate(dayjs(date));
                    }
                    if (extractedData.invoiceDueDate && extractedData.invoiceDueDate !== 'not_found') {
                        const dueDate = moment(extractedData.invoiceDueDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
                        setDueDate(dayjs(dueDate));
                    }

                    setCurrency(extractedData.invoiceCurrency);

                    // Normalize invoiceTotal
                    let invoiceTotal = extractedData.invoiceTotal;
                    if (invoiceTotal && typeof invoiceTotal === 'string') {
                        if (invoiceTotal.includes(',')) {
                            const parts = invoiceTotal.split(',');
                            if (parts.length > 1 && parts[parts.length - 1].length === 2) {
                                invoiceTotal = invoiceTotal.replace(/\./g, '').replace(',', '.');
                            } else {
                                invoiceTotal = invoiceTotal.replace(/,/g, '');
                            }
                        } else if (invoiceTotal.includes('.')) {
                            const parts = invoiceTotal.split('.');
                            if (parts.length > 1 && parts[parts.length - 1].length === 2) {
                                // already correct format
                            } else {
                                invoiceTotal = invoiceTotal.replace(/\./g, '');
                            }
                        }
                    }

                    setAmount(invoiceTotal);

                    setLoading(false);
                    setBase64Jpg(null);
                } catch (error) {
                    console.error('Error parsing PDF:', error);
                    setLoading(false);
                    setBase64Jpg(null);
                }
            };

            parseInvoice();
        }
    }, [base64Jpg]);

    const findBestMatchVendor = async (extractedCompanyName, vendors) => {
        const foundedVendor = findMatchingVendor(vendors, extractedCompanyName);

        if (foundedVendor === undefined || foundedVendor === null) {
            await handleNewVendor(extractedCompanyName);
        }
        else {
            const existingVendor = foundedVendor;
            if (existingVendor?.creationDate) {
                //check if the creation date is today then setIsNewVendor to true
                const creationDate = new Date(foundedVendor.creationDate);
                const today = new Date();
                if (creationDate.toDateString() === today.toDateString()) {
                    setIsNewVendor(true);
                } else {
                    setIsNewVendor(false);
                }
            }
            setVendor(existingVendor.vendor);
        }
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();

        if (comments === '' || comments.length === 0 || comments.trim() === '' || comments === null || comments === undefined) {
            alert('Please enter a comment to submit an invoice');
            return;
        }

        setLoading(true);

        let formattedAmount = amount.replace(/[^0-9.]/g, '');
        formattedAmount = parseFloat(formattedAmount).toFixed(2);
        formattedAmount = formattedAmount.replace(/\.00$/, '');

        const vendorData = vendors.find(v => v.vendor === vendor) || findMatchingVendor(vendors, vendor);

        if (!vendorData) {
            alert('Please select a valid vendor');
            setLoading(false);
            return;
        }

        const selectedApprovalController = controllers.find((controller) => controller.controller === financialController);
        const configuredFinancialController = controllers.find((controller) => (
            controller.role === FINANCIAL_CONTROLLER_ROLE
        ));
        const complianceCheckController = controllers.find((controller) => (
            controller.role === COMPLIANCE_CHECK_ROLE
        ));

        if (!vendorData.recurring && !configuredFinancialController) {
            alert('Please configure a Financial Controller before submitting an invoice');
            setLoading(false);
            return;
        }

        if (!vendorData.recurring && !selectedApprovalController) {
            alert('Please select an approval controller');
            setLoading(false);
            return;
        }

        if (isNewVendor === true && !complianceCheckController) {
            alert('Please configure a Compliance Check controller before submitting a new vendor invoice');
            setLoading(false);
            return;
        }

        const selectedIsFinancialController = selectedApprovalController?.role === FINANCIAL_CONTROLLER_ROLE;

        if (!vendorData.recurring) {
            const users = await getUsers();
            const controllersToValidate = [
                selectedApprovalController,
                configuredFinancialController,
                isNewVendor === true ? complianceCheckController : null,
            ].filter(Boolean);
            const missingController = controllersToValidate.find((controller) => (
                !controllerExistsInUsers(users, controller)
            ));

            if (missingController) {
                showAlert('error', `User ${getControllerDisplayName(missingController)} does not exist`);
                setLoading(false);
                return;
            }
        }

        const formData = {
            company: company,
            vendor_id: vendorData.id,
            invoice_number: invoiceNumber,
            invoice_date: invoiceDate,
            invoice_type: vendorData.type,
            due_date: dueDate,
            currency,
            amount: formattedAmount,
            status: vendorData.recurring ? 'Approved' : isNewVendor === true ? 'Pending Compliance Check' : selectedIsFinancialController ? 'Pending' : 'Pending First Approval',
            sender: sender,
            sender_email: user?.username,
            sender_comment: comments,
            sender_date: new Date(),
            approved_by: vendorData.recurring ? 'System' : null,
            approval_comment: vendorData.recurring ? 'Automatically approved by the system due to recurring invoice' : null,
            approved_date: vendorData.recurring ? new Date() : null,
            file_name: file.name,
            first_approver: selectedIsFinancialController ? '' : financialController,
            compliance_check_controller: isNewVendor === true ? complianceCheckController.controller : null,
        };

        const onedrive_folder = vendorData.recurring ? folderVariables.invoiceApprovalFolder : folderVariables.invoiceFolder;
        const companyFolder = company === 'We Soda Ltd' ? folderVariables.invoiceFolder : company === 'Soda World Ltd' ? folderVariablesSodaWorld.invoiceFolder : folderVariables.invoiceApprovalFolder;

        try {
            const invoiceId = await insertInvoiceData(formData);

            // Handle errors appropriately within insertInvoiceData function
            if (errorMessages.length > 0) {
                alert('Error inserting invoice');
                setLoading(false);
                return;
            }

            await uploadInvoicePdf(file, accessToken, refreshToken, companyFolder, setAccessToken, validateToken);

            await uploadAzureBlob(file);

            if (!vendorData.recurring && !isNewVendor) {
                await sendEmail(invoiceId, selectedApprovalController.email, user?.name, vendor, user?.username, bccEmails);
            }
            else if (isNewVendor) {
                await sendEmail(invoiceId, complianceCheckController.email, user?.name, vendor, user?.username, bccEmails);
            }

            setLoading(false);
            navigate('/invoice-history');
        } catch (error) {
            console.error('Error submitting form:', error);
            alert('An error occurred while submitting the form');
            setLoading(false);
        }
    }

    const insertInvoiceData = async (invoice) => {
        setLoading(true);
        try {
            const invoiceResponse = await insertInvoice(invoice);
            return invoiceResponse.id;
        } catch (error) {
            setErrorMessages([...errorMessages, 'Error inserting invoice']);
            // Handle error, show error message, etc.
        }
        setLoading(false);
    }

    const handleFinancialControllerChange = (value) => {
        setFinancialController(value);
    }

    const approvalRoleSortOrder = {
        [FINANCIAL_CONTROLLER_ROLE]: 1,
        [FIRST_APPROVER_ROLE]: 2,
    };

    const approvalControllerOptions = controllers
        .filter((controller) => (
            controller.role === FINANCIAL_CONTROLLER_ROLE ||
            controller.role === FIRST_APPROVER_ROLE
        ))
        .sort((a, b) => (
            (approvalRoleSortOrder[a.role] || 99) - (approvalRoleSortOrder[b.role] || 99) ||
            a.controller.localeCompare(b.controller)
        ));

    const bccEmails = controllers
        .filter((controller) => controller.role === ACCOUNTING_EMAIL_ROLE)
        .map((controller) => controller.email)
        .filter(Boolean);

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'copy';
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const files = e.dataTransfer.files;
        if (files.length > 0 && files[0].type === 'application/pdf') {
            setFile(files[0]);
        } else {
            Modal.error({ title: 'Invalid File', content: 'Please upload a valid PDF file.' });
        }
    };

    const handleFileInput = (e) => {
        const file = e.target.files[0];
        if (file && file.type === 'application/pdf') {
            setFile(file);
        } else {
            Modal.error({ title: 'Invalid File', content: 'Please upload a valid PDF file.' });
        }
    };

    if (loading) {
        return <Spin
            size="large"
            style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flex: 1, marginTop: -100 }}
        />;
    }

    return (
        <div className="invoice-create-page">
            {activeTab === 'upload' && file === null && (
                <div className="page-heading-row">
                    <div className="page-heading-copy">
                        <span className="page-kicker">Create invoice</span>
                        <h1>New Invoice</h1>
                        <p>Upload a PDF, confirm the extracted details, then route it to the right approver.</p>
                    </div>
                </div>
            )}
            <StepIndicator activeTab={activeTab} />
            {activeTab === 'upload' && (
                file === null ? (
                    <div
                        className="invoice-upload-card"
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                    >
                        <span className="upload-card-kicker">PDF upload</span>
                        <h2>Drop an invoice here</h2>
                        <p>Select or drag a PDF invoice. The first page will be read to prefill the form.</p>
                        <input
                            type="file"
                            id="file"
                            accept=".pdf"
                            onChange={handleFileInput}
                            style={{ display: 'none' }}
                        />
                        <button className="primary-action-button" onClick={() => document.getElementById('file').click()}>Add Document</button>
                    </div>
                ) : (
                    <div className="invoice-create-workspace">
                        <div className="invoice-create-toolbar">
                            <button onClick={goBack} className="detail-back-button">
                                <FontAwesomeIcon icon={faArrowLeft} /> Back
                            </button>
                        </div>
                        <div className="invoice-create-layout">
                            <section className="invoice-preview-panel">
                                {file && <PDFPreview pdfFile={file} />}
                            </section>
                            <aside className="invoice-form-panel">
                                <InvoiceForm
                                    company={company}
                                    vendor={vendor}
                                    vendorOptions={vendors}
                                    companyOptions={companies}
                                    invoiceNumber={invoiceNumber}
                                    invoiceDate={invoiceDate}
                                    dueDate={dueDate}
                                    currency={currency}
                                    currencyOptions={['GBP', 'USD', 'EUR', 'TL', 'TRY']}
                                    amount={amount}
                                    handleVendorChange={handleVendorChange}
                                    handleCompanyChange={handleCompanyChange}
                                    handleInvoiceNumberChange={handleInvoiceNumberChange}
                                    handleInvoiceDateChange={handleInvoiceDateChange}
                                    handleDueDateChange={handleDueDateChange}
                                    handleCurrencyChange={handleCurrencyChange}
                                    handleAmountChange={handleAmountChange}
                                    handleSubmit={handleSubmit}
                                    addNewVendor={handleNewVendor}
                                    marginLeft={'0'}
                                />
                            </aside>
                        </div>
                    </div>
                )
            )}
            {activeTab === 'submit' && (
                <div className="invoice-submit-layout">
                    <section className="invoice-submit-panel">
                        <SubmitForm
                            onSubmit={handleFormSubmit}
                            value={comments}
                            onChange={(e) => setComments(e.target.value)}
                            goBack={goBackSubmit}
                        />
                    </section>
                    <section className="invoice-approval-panel">
                        <FinancialApproval value={financialController} onChange={handleFinancialControllerChange} controllerOptions={approvalControllerOptions} />
                    </section>
                </div>
            )}
        </div>
    );
};

export default Invoice;
